import React from "react";
import { Link } from "react-router-dom";
import { getBrand } from "../../utils/branding";
import { getLegalContent } from "../../utils/legalContent";
import Logo from "../../components/Logo";
import Footer from "../../components/Footer";
import { ArrowLeft, AlertTriangle } from "lucide-react";

export default function Terms() {
  const brand = getBrand();
  const c = getLegalContent();

  return (
    <div className="au-root">
      <header style={hdr}>
        <Link to="/" style={{ textDecoration: "none" }}><Logo size="medium" /></Link>
        <Link to="/" className="au-pill au-pill-glass au-pill-sm">
          <ArrowLeft /> Back to Home
        </Link>
      </header>

      <div className="au-container" style={{ maxWidth: 820 }}>
        <div className="au-glass au-pad-lg" style={{ marginBottom: 56 }}>
          <div className="au-eyebrow" style={{ marginBottom: 14 }}>
            TERMS · LAST UPDATED {c.lastUpdated.toUpperCase()}
          </div>
          <h1 style={h1}>Terms of <span style={{ color: "var(--au-accent-ink)" }}>Service</span></h1>
          <p className="au-lead" style={{ marginTop: 16 }}>
            By using {c.fullName}, you agree to these terms. Please read carefully.
          </p>

          <S n="1" t="Acceptance of Terms">
            <p>By accessing or using {c.fullName} ("Service"), you agree to be bound by these Terms. If you don't agree, don't use the Service.</p>
          </S>

          <S n="2" t="Eligibility">
            <ul style={ul}>
              <li>You must be 18 years or older</li>
              <li>You must provide accurate registration information</li>
              <li>You must not be banned from any prior {c.fullName} account</li>
              {c.kycRequired && <li>You must complete KYC verification for full access</li>}
            </ul>
          </S>

          <S n="3" t="Acceptable Use">
            <p>You may use the Service ONLY to {c.actionVerb}:</p>
            <ul style={ul}>
              <li>Targets you own</li>
              <li>Targets you have explicit written permission to {c.actionVerb}</li>
              <li>Targets explicitly listed in a public bug bounty program</li>
            </ul>
            <p style={warn}>
              <AlertTriangle size={16} style={icoInline} />
              Unauthorized {c.actionVerb}ning of third-party systems is illegal under the IT Act 2000 and Computer Fraud laws. Violation will result in immediate account termination and may be reported to law enforcement.
            </p>
          </S>

          <S n="4" t="Account Responsibility">
            <ul style={ul}>
              <li>You're responsible for all activity under your account</li>
              <li>Keep your login email secure</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>One person, one account — no sharing</li>
            </ul>
          </S>

          {c.paymentSupported && (
            <S n="5" t="Payment & Billing">
              <ul style={ul}>
                <li>Subscriptions billed monthly/yearly as selected</li>
                <li>Auto-renewal unless cancelled before billing date</li>
                <li>Cancel anytime from Settings</li>
                <li>Refunds per our <Link to="/refund" style={link}>Refund Policy</Link></li>
                <li>Prices may change with 30 days notice</li>
              </ul>
            </S>
          )}

          <S n={c.paymentSupported ? "6" : "5"} t="Service Availability">
            <p>We aim for 99.5% uptime but cannot guarantee uninterrupted service. We may:</p>
            <ul style={ul}>
              <li>Schedule maintenance with prior notice</li>
              <li>Update features and pricing</li>
              <li>Suspend accounts for violations</li>
              <li>Discontinue the Service with 60 days notice</li>
            </ul>
          </S>

          <S n={c.paymentSupported ? "7" : "6"} t="Intellectual Property">
            <p>All Service code, design, and trademarks belong to {c.parentCompany}. Your {c.actionVerb} reports and data belong to YOU.</p>
          </S>

          <S n={c.paymentSupported ? "8" : "7"} t="Limitation of Liability">
            <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
            <ul style={ul}>
              <li>Service provided "as is" without warranties</li>
              <li>We're not liable for indirect or consequential damages</li>
              <li>Maximum liability limited to fees paid in last 12 months</li>
              <li>You agree to defend us against claims arising from your misuse</li>
            </ul>
          </S>

          <S n={c.paymentSupported ? "9" : "8"} t="Termination">
            <p>We may terminate your account if you:</p>
            <ul style={ul}>
              <li>Violate these Terms</li>
              <li>Engage in unauthorized {c.actionVerb}ning</li>
              <li>Fail to pay fees</li>
              <li>Use the Service for illegal activity</li>
            </ul>
            <p>You may cancel anytime. Data deleted per <Link to="/privacy" style={link}>Privacy Policy</Link>.</p>
          </S>

          <S n={c.paymentSupported ? "10" : "9"} t="Governing Law">
            <p>These Terms are governed by laws of {c.jurisdiction}. Disputes resolved in courts of {c.jurisdiction}.</p>
          </S>

          <S n={c.paymentSupported ? "11" : "10"} t="Contact">
            <p>Questions? Email <a href={`mailto:${c.legalEmail}`} style={link}>{c.legalEmail}</a></p>
            <p style={metaMuted}>{c.fullName} · {c.address}</p>
          </S>

        </div>
      </div>
      <Footer />
    </div>
  );
}

function S({ n, t, children }) {
  return (
    <div style={section}>
      <h2 style={h2}>
        <span style={{ color: "var(--au-accent-ink)", marginRight: 10 }}>{n}.</span>{t}
      </h2>
      <div style={body}>{children}</div>
    </div>
  );
}

const hdr = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, maxWidth: 1200, margin: "0 auto", padding: "28px 24px 8px" };
const h1 = { fontSize: 42, margin: 0 };
const section = { marginTop: 36, paddingTop: 28, borderTop: "1px solid var(--au-hair)" };
const h2 = { fontSize: 20, marginBottom: 16, fontWeight: 700 };
const body = { color: "var(--au-text-2)", fontSize: 14, lineHeight: 1.8 };
const ul = { margin: "8px 0", paddingLeft: 22 };
const link = { color: "var(--au-accent-ink)", textDecoration: "none", fontWeight: 600 };
const warn = { color: "var(--au-error)", fontWeight: 600, marginTop: 12, display: "flex", alignItems: "flex-start", gap: 8 };
const icoInline = { flex: "none", marginTop: 3 };
const metaMuted = { fontSize: 13, color: "var(--au-muted)", marginTop: 8 };
const copyright = { marginTop: 48, paddingTop: 24, borderTop: "1px solid var(--au-hair)", fontSize: 12, color: "var(--au-muted)", textAlign: "center" };
